import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { EmptyState, LoadingSkeleton, ConfirmDialog } from "@/components/ui-system";
import { RateMath } from "@/components/b2c/RateMath";
import { toast } from "sonner";
import {
  Truck, ChevronDown, ChevronUp, Wallet, AlertTriangle, PackageX, RefreshCw, Zap, IndianRupee,
} from "lucide-react";

const inr = (n) =>
  `₹${Number(n ?? 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const errDetail = (error, fallback) => {
  const d = error?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d) && d[0]?.msg) return d[0].msg;
  return error?.response?.data?.message || fallback;
};

/**
 * Shared courier-select step used by the create flow, the order detail page, and (read-only)
 * the rate calculator. Posts the quote, renders white-labeled courier options with an expandable
 * landed-cost breakdown, and (when an orderId + onShipped are given) ships the order.
 *
 * White-label: only canonical courier name/logo/code are ever shown — no aggregator identity.
 *
 * props:
 *  - quoteInput: { pickup_location_id?|pickup_pincode?, drop_pincode, payment_mode, order_value, package }
 *  - orderId?: string         — when present the "Ship with …" CTA ships the order
 *  - walletBalance?: number   — inline balance check; ship disabled when total exceeds it
 *  - onShipped?: (order) => void
 *  - readOnly?: bool          — rate-calculator mode: render options, no ship CTA
 *  - autoQuote?: bool (default true) — fetch on mount / when quoteInput changes
 */
export function CourierSelect({
  quoteInput,
  orderId,
  walletBalance,
  onShipped,
  readOnly = false,
  autoQuote = true,
  // Which API surface to quote/ship against. Sellers use the default seller
  // routes; the admin create-on-behalf page passes apiBase="b2c/admin" and
  // extraBody={{ seller_id }} so the same picker drives the admin endpoints.
  apiBase = "b2c/seller",
  extraBody = null,
  // Whose wallet the ship debits — the seller sees "your wallet"; the admin
  // create-on-behalf page passes "the seller's wallet".
  walletLabel = "your wallet",
}) {
  const [loading, setLoading] = useState(false);
  const [quote, setQuote] = useState(null); // { zone, charged_weight_kg, volumetric_kg, options }
  const [error, setError] = useState(null);
  const [noPartner, setNoPartner] = useState(false);
  const [expanded, setExpanded] = useState(null); // option key whose breakdown is open
  const [sortBy, setSortBy] = useState("cheapest"); // 'cheapest' | 'fastest'
  const [shipping, setShipping] = useState(false);
  const [confirmOption, setConfirmOption] = useState(null);

  const optionKey = (o) => `${o.courier_code}::${o.mode}`;

  const fetchQuote = useCallback(async () => {
    if (!quoteInput || !quoteInput.drop_pincode) return;
    setLoading(true);
    setError(null);
    setNoPartner(false);
    try {
      const { data } = await axios.post(`${API}/${apiBase}/orders/quote`, { ...quoteInput, ...(extraBody || {}) });
      setQuote(data);
    } catch (e) {
      const status = e?.response?.status;
      const msg = errDetail(e, "Could not fetch courier rates");
      if (status === 422) {
        // 422 here means no plan / no serviceable couriers — surface honestly, not as a hard error.
        setNoPartner(true);
        setError(msg);
      } else {
        setError(msg);
        toast.error(msg);
      }
      setQuote(null);
    } finally {
      setLoading(false);
    }
    // extraBody must be a STABLE reference (memoize it in the caller) or this refetches every render.
  }, [quoteInput, apiBase, extraBody]);

  useEffect(() => {
    if (autoQuote) fetchQuote();
  }, [autoQuote, fetchQuote]);

  const sortedOptions = useMemo(() => {
    const opts = [...(quote?.options || [])];
    if (sortBy === "fastest") {
      // EDD not available in E1 plan-only quotes; "fastest" falls back to Air-first then cheapest.
      opts.sort((a, b) => {
        const am = (a.mode || "").toLowerCase() === "air" ? 0 : 1;
        const bm = (b.mode || "").toLowerCase() === "air" ? 0 : 1;
        if (am !== bm) return am - bm;
        return (a.total ?? 0) - (b.total ?? 0);
      });
    } else {
      opts.sort((a, b) => (a.total ?? 0) - (b.total ?? 0));
    }
    return opts;
  }, [quote, sortBy]);

  const cheapestKey = useMemo(() => {
    let best = null;
    for (const o of quote?.options || []) {
      if (best === null || (o.total ?? Infinity) < (best.total ?? Infinity)) best = o;
    }
    return best ? optionKey(best) : null;
  }, [quote]);

  const doShip = async (option) => {
    if (!orderId) return;
    setShipping(true);
    try {
      const { data } = await axios.post(`${API}/${apiBase}/orders/${orderId}/ship`, {
        courier_code: option.courier_code,
        mode: option.mode,
        ...(extraBody || {}),
      });
      toast.success(`Shipment booked — AWB ${data.awb || "assigned"}`);
      setConfirmOption(null);
      onShipped?.(data);
    } catch (e) {
      const status = e?.response?.status;
      const msg = errDetail(e, "Could not ship this order");
      if (status === 422) {
        // No enabled partner / not serviceable — order stays New, nothing debited.
        toast.error(msg);
        setNoPartner(true);
      } else if (status === 402) {
        toast.error(msg || "Insufficient wallet balance");
      } else if (status === 409) {
        toast.error(msg || "This order is no longer in a shippable state");
        onShipped?.(e?.response?.data);
      } else {
        toast.error(msg);
      }
      setConfirmOption(null);
    } finally {
      setShipping(false);
    }
  };

  if (loading) {
    return (
      <div data-testid="courier-select-loading">
        <LoadingSkeleton variant="table" rows={4} cols={4} />
      </div>
    );
  }

  if (noPartner) {
    return (
      <Card className="border-0 shadow-sm" data-testid="courier-select-no-partner">
        <CardContent className="p-0">
          <EmptyState
            icon={PackageX}
            title="No courier available for this lane yet"
            description={
              error ||
              "No courier can service this route right now — your order is saved as New. You can ship it as soon as a courier becomes available for this lane."
            }
            action={
              <Button variant="outline" onClick={fetchQuote} data-testid="courier-select-retry">
                <RefreshCw className="w-4 h-4 mr-2" /> Try again
              </Button>
            }
          />
        </CardContent>
      </Card>
    );
  }

  if (error && !quote) {
    return (
      <Card className="border-0 shadow-sm" data-testid="courier-select-error">
        <CardContent className="p-0">
          <EmptyState
            icon={AlertTriangle}
            title="Couldn't load courier rates"
            description={error}
            action={
              <Button variant="outline" onClick={fetchQuote} data-testid="courier-select-retry">
                <RefreshCw className="w-4 h-4 mr-2" /> Retry
              </Button>
            }
          />
        </CardContent>
      </Card>
    );
  }

  if (quote && (!quote.options || quote.options.length === 0)) {
    return (
      <Card className="border-0 shadow-sm" data-testid="courier-select-empty">
        <CardContent className="p-0">
          <EmptyState
            icon={Truck}
            title="No couriers serve this lane"
            description={`We could not find a courier for ${quote.zone ? `zone ${quote.zone}` : "this route"} at ${
              quote.charged_weight_kg ?? "—"
            } kg. Check the pincodes or package details.`}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="courier-select">
      {/* Quote summary + sort */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-wrap items-center gap-2 text-sm text-slate-600">
          {quote?.zone && (
            <Badge variant="outline" className="border-slate-200 text-slate-600">
              Zone {quote.zone}
            </Badge>
          )}
          {quote?.charged_weight_kg != null && (
            <span className="text-slate-700">
              Charged weight: <span className="font-semibold tabular-nums">{quote.charged_weight_kg} kg</span>
            </span>
          )}
          {quote?.volumetric_kg != null && (
            <span className="text-slate-400">(volumetric {quote.volumetric_kg} kg)</span>
          )}
          {quote?.serviceable_count != null ? (
            <Badge className="border-0 bg-green-100 text-green-800 text-xs" data-testid="serviceable-count">
              {quote.serviceable_count} {quote.serviceable_count === 1 ? "courier" : "couriers"} serviceable
            </Badge>
          ) : (
            sortedOptions.length > 0 && (
              <span className="text-slate-500" data-testid="courier-count">
                {sortedOptions.length} {sortedOptions.length === 1 ? "courier" : "couriers"}
              </span>
            )
          )}
        </div>
        <div className="flex gap-3" role="group" aria-label="Sort couriers">
          <Button
            type="button"
            size="sm"
            variant={sortBy === "cheapest" ? "default" : "outline"}
            onClick={() => setSortBy("cheapest")}
            data-testid="courier-sort-cheapest"
          >
            <IndianRupee className="w-3.5 h-3.5 mr-1" /> Cheapest
          </Button>
          <Button
            type="button"
            size="sm"
            variant={sortBy === "fastest" ? "default" : "outline"}
            onClick={() => setSortBy("fastest")}
            data-testid="courier-sort-fastest"
          >
            <Zap className="w-3.5 h-3.5 mr-1" /> Fastest
          </Button>
        </div>
      </div>

      {/* Wallet hint (ship mode only) */}
      {!readOnly && walletBalance != null && (
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Wallet className="w-3.5 h-3.5" />
          Wallet balance: <span className="font-semibold text-slate-700">{inr(walletBalance)}</span>
        </div>
      )}

      {/* Courier option cards */}
      <div className="space-y-3">
        {sortedOptions.map((o) => {
          const key = optionKey(o);
          const isOpen = expanded === key;
          const isCheapest = key === cheapestKey;
          const insufficient = !readOnly && walletBalance != null && Number(o.total) > Number(walletBalance);
          return (
            <Card
              key={key}
              className="border border-slate-200 shadow-sm"
              data-testid={`courier-option-${o.courier_code}-${o.mode}`}
            >
              <CardContent className="p-4">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  {/* Courier identity */}
                  <div className="flex items-center gap-3 min-w-0">
                    {o.logo_url ? (
                      <img
                        src={o.logo_url}
                        alt={o.name || o.courier_code}
                        className="h-9 w-9 rounded object-contain bg-slate-50 p-0.5 shrink-0"
                      />
                    ) : (
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-brand/10 text-brand">
                        <Truck className="h-5 w-5" />
                      </span>
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-slate-800 truncate">{o.name || o.courier_code}</span>
                        {isCheapest && (
                          <Badge className="bg-brand/10 text-brand hover:bg-brand/10 border-0 text-[10px]">
                            Recommended
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 capitalize">
                        {o.mode || "surface"} · {o.charged_weight_kg ?? quote?.charged_weight_kg} kg
                      </div>
                    </div>
                  </div>

                  {/* Price + action */}
                  <div className="flex items-center justify-between gap-3 sm:justify-end">
                    <div className="text-right">
                      <div className="text-lg font-heading font-bold tabular-nums text-slate-800">
                        {inr(o.total)}
                      </div>
                      <button
                        type="button"
                        onClick={() => setExpanded(isOpen ? null : key)}
                        className="text-xs text-brand hover:underline inline-flex items-center gap-0.5"
                        data-testid={`courier-breakdown-toggle-${o.courier_code}-${o.mode}`}
                      >
                        Breakdown {isOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                      </button>
                    </div>
                    {!readOnly && orderId && (
                      <Button
                        type="button"
                        size="sm"
                        disabled={insufficient || shipping}
                        onClick={() => setConfirmOption(o)}
                        data-testid={`courier-ship-${o.courier_code}-${o.mode}`}
                      >
                        Ship
                      </Button>
                    )}
                  </div>
                </div>

                {insufficient && (
                  <p className="mt-2 text-xs text-red-600 font-medium flex items-center gap-1">
                    <AlertTriangle className="w-3.5 h-3.5" /> Wallet balance too low for this courier — recharge to ship.
                  </p>
                )}

                {/* Expandable breakdown — shows the full freight/COD/GST math, not just totals */}
                {isOpen && <RateMath rate={o} className="mt-3" />}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <ConfirmDialog
        open={!!confirmOption}
        onOpenChange={(v) => !v && setConfirmOption(null)}
        title={`Ship with ${confirmOption?.name || confirmOption?.courier_code}?`}
        description={
          confirmOption
            ? `${inr(confirmOption.total)} will be debited from ${walletLabel} and an AWB will be assigned. This books a real shipment.`
            : ""
        }
        confirmLabel={`Ship for ${inr(confirmOption?.total)}`}
        loading={shipping}
        onConfirm={() => doShip(confirmOption)}
      />
    </div>
  );
}

export default CourierSelect;
